import type { IEmailNotification, ILabInvite, IOrderCompletedNotification, IProjectInvite } from '../../../Model/Invite/IInvite.js'
import type React from 'react'
import { Link } from 'react-router-dom'

interface CompactInviteProps<T extends IEmailNotification> {
  type: 'SEND' | 'RECEIVE'
  invite: T
}

export const ProjectInviteCompact: React.FC<CompactInviteProps<IProjectInvite>> = ({ type, invite }) => {
  const description =
    type == 'RECEIVE'
      ? `You have been invited to collaborate on a project with ${invite.mayEdit ? 'edit permissions.' : 'view-only access.'}`
      : `You have invited someone to collaborate on a project with ${invite.mayEdit ? 'edit permissions.' : 'view-only access.'}`

  return (
    <div data-testid={`invite-compact-project-${type.toLowerCase()}-${invite.id}`} key={invite.id} className="bg-white border rounded-lg p-4 mb-4 hover:bg-gray-50 shadow-sm">
      <Link to={`/invite/${invite.id}`} className="block">
        <h3 className="text-lg font-semibold">Project Invitation</h3>
        <p className="text-gray-600">{description}</p>
        <p className="text-sm text-gray-500 mt-2">
          {invite.additionalText && invite.additionalText.length > 100
            ? `${invite.additionalText.substring(0, 100)}...`
            : invite.additionalText}
        </p>
      </Link>
    </div>
  )
}

export const OrderCompletedNotificationCompact: React.FC<CompactInviteProps<IOrderCompletedNotification>> = ({ type, invite }) => {
  return (
    <div data-testid={`invite-compact-order-completed-${type.toLowerCase()}-${invite.id}`} className="bg-white border rounded-lg p-4 mb-4 hover:bg-gray-50 shadow-sm">
      <Link to={`/invite/${invite.id}`} className="block">
        <h3 className="text-lg font-semibold">Order Completion Notification</h3>
        <p className="text-gray-600">
          {type == 'RECEIVE' ? (
            `You will receive a notification if order ${invite.orderName} completes.`
          ) : (
            <span>
              Customer: {invite.email}
              <br />
              Order: {invite.orderName}
            </span>
          )}
        </p>
      </Link>
    </div>
  )
}

export const LabInviteNotificationCompact: React.FC<CompactInviteProps<ILabInvite>> = ({ type, invite }) => {
  if (!invite.order) return 'No order found for this invite.'

  return (
    <div data-testid={`invite-compact-lab-invite-${type.toLowerCase()}-${invite.id}`} className="bg-white border rounded-lg p-4 mb-4 hover:bg-gray-50 shadow-sm">
      <Link to={`/invite/${invite.id}`} className="block">
        <h3 className="text-lg font-semibold">Lab Invitation {invite.order.name}</h3>
        <p className="text-gray-600">
          {type == 'RECEIVE'
            ? `You have been invited to work on an order by ${invite.inviterUser?.email}`
            : `You have invited organization ${invite.invitedOrg?.name} to process your order`}
        </p>
      </Link>
    </div>
  )
}
